import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import { post } from '@util/xhrAPI'
import { updateSterilizationCallFormObject, clearSterilizationCallForm, updateErrors, updateSterilizationPayment } from "@store/slice/sterilizationSlice"
import { updateNotification, updateValidationPage } from '@store/slice/mainSlice'
import { useEffect, useState } from "react"
import { useNavigate } from "react-router"
import { ROLE_PUBLIC } from "@router/Routes"
import useMainData from '@hook/useMainData'
import useStoreUpdate from "@hook/useStoreUpdate"
import useToggle from "@hook/useToggle"
import useFormData from "@hook/useFormData"
import useHtmlRenderer from '@hook/useHtmlRenderer'
import useGoogleRecaptchaTokenizer from "@hook/useGoogleRecaptchaTokenizer"
import useCostCalculator from "@hook/useCostCalculator"
import Div from '@ui/layout/Div'
import Form from '@ui/elements/Form'
import Alert from '@ui/elements/Alert'
import sterilizationCitizenCallFormFields, { sterilizationCitizenCallSaveUrl } from "@form/sterilizationCitizenCallFormFields"

const CitizenSterilizationCallMaker = () => {
  const { sterilizationCitizenCallFormObject, errors } = useSelector(state => state.sterilization)
  const { data, mission, missionBillings, rgpdPage } = useSelector(state => state.main)
  const { paymentConfig } = mission
  const [userCoordinates, setUserCoordinates] = useState(null)
  const [isSurfaceBasedCost, setIsSurfaceBasedCost] = useState(false)
  const [cost, setCost] = useState(null)
  const [unitsToBill, setUnitsToBill] = useState(null)
  const dispatch = useDispatch()
  const loadData = useMainData()
  const calculateCost = useCostCalculator()
  const [updateFormObject, updateFormObjectProp] = useStoreUpdate(dispatch, updateSterilizationCallFormObject, sterilizationCitizenCallFormObject)
  const [isLoading, toggleLoading] = useToggle(false)
  const [t] = useTranslation(['sterilization', 'common'])
  const renderFormData = useFormData()
  const navigate = useNavigate()
  const renderHtml = useHtmlRenderer()
  const [invokeChallenge, appendScript] = useGoogleRecaptchaTokenizer()

  const renderCost = () => {
    if (!cost) return <></>
    let messageCode = 'form.nests_number_help'
    if (isSurfaceBasedCost) {
      messageCode = 'form.surface_number_help'
    }
    return <>
      <Alert
        message={t(messageCode, { unitsNumber: sterilizationCitizenCallFormObject.unitsNumber, cost: cost })}
        type='is-info'
      />
    </>
  }

  const generateFormStep = step => {
    return sterilizationCitizenCallFormFields(
      step,
      sterilizationCitizenCallFormObject,
      updateFormObject,
      updateFormObjectProp,
      errors,
      saveForm,
      1 === parseInt(paymentConfig['defaultPayment']?.isBilled),
      isSurfaceBasedCost,
      isLoading,
      t,
      data,
      cost,
      renderCost,
      mission
    )
  }

  const fetchInitialData = () => {
    const dataObject = {
      cities: 'cities',
      districts: 'districts',
      nuisances: 'nuisances',
      billingTypes: 'billingTypes',
    }
    loadData(dataObject)
  }

  const saveForm = async () => {
    toggleLoading()
    const token = await invokeChallenge(updateFormObjectProp)
    const callObject = { ...sterilizationCitizenCallFormObject, 'g-recaptcha-response': token }
    post(sterilizationCitizenCallSaveUrl, renderFormData(callObject, ['userCoordinates', 'coordinates']), response => {
      const { validationPage, sterilizationOrder, paymentModes, paymentPage, code, errors } = response
      if (200 === code) {
        dispatch(updateNotification({ type: 'is-success', message: t('messages.call_created_successfuly') }))
        dispatch(updateValidationPage(validationPage))
        dispatch(clearSterilizationCallForm())
        if (sterilizationOrder && sterilizationOrder.billedAmount) {
          dispatch(updateSterilizationPayment({ sterilizationOrder, paymentModes, paymentPage }))
          navigate(ROLE_PUBLIC['paymentPage'])
          window.localStorage.setItem('sterilizationOrder', sterilizationOrder.reference)
        } else {
          navigate(ROLE_PUBLIC['publicValidation'])
        }
      } else {
        if (!Array.isArray(errors)) {
          console.log(errors)
          dispatch(updateErrors(errors))
        }
        dispatch(updateNotification({ type: 'is-danger', message: t(`messages.${errors.length ? errors[0] : 'errors_encountered'}`) }))
      }

      toggleLoading()
    })
  }

  const updateUserCoordinates = () => {
    if (window.innerWidth <= 768) { //get user location if on mobile
      navigator.geolocation.getCurrentPosition(function (position) {
        setUserCoordinates({ lat: position.coords.latitude, lng: position.coords.longitude })
      })
    }
  }

  useEffect(() => {
    updateFormObjectProp('mission', mission.id)
    fetchInitialData()
    updateUserCoordinates()
    appendScript()
  }, [])

  useEffect(() => {
    if (data.billingTypes.length) {
      const billingType = data.billingTypes.filter(billingType => billingType.value === paymentConfig['defaultPayment']?.billingType)[0]
      setIsSurfaceBasedCost(billingType?.isSurfaceBased)
    }
  }, [data])

  useEffect(() => {
    if (userCoordinates) {
      updateFormObjectProp('userCoordinates', userCoordinates)
    }
  }, [userCoordinates])

  useEffect(() => {
    if (
      1 === parseInt(paymentConfig['defaultPayment']?.isBilled)
      && sterilizationCitizenCallFormObject.unitsNumber
      && sterilizationCitizenCallFormObject.unitsNumber != unitsToBill
    ) {
      setCost(calculateCost(
        parseFloat(sterilizationCitizenCallFormObject.unitsNumber),
        Object.values(missionBillings).filter(billing => {
          return isSurfaceBasedCost ? billing.isSurfaceBased : !billing.isSurfaceBased
        }),
        sterilizationCitizenCallFormObject.callType
      ))
      setUnitsToBill(sterilizationCitizenCallFormObject.unitsNumber)
    }

  }, [sterilizationCitizenCallFormObject])

  return (
    <>
      <Div cssCalss='sterilization-request-maker'>
        <h2 className="scene-title">{t('titles.citizen_sterilization_call_maker')}</h2>
        <Form cssClass='citizen-sterilization-call columns'>
          <Div cssClass='left-form column is-6'>
            <Div cssClass='columns is-multiline'>
              {generateFormStep('caller')}
            </Div>
          </Div>
          <Div cssClass='right-form column is-6'>
            <Div cssClass='columns is-multiline'>
              {generateFormStep('call')}
            </Div>
          </Div>
        </Form>
        <Div cssCalss='cms-content'>
          {renderHtml(rgpdPage.description)}
        </Div>
      </Div>
    </>
  )
}

export default CitizenSterilizationCallMaker